import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as PlayIcon } from "../../assets/icons/svg/record-start.svg";
import { ReactComponent as StopIcon } from "../../assets/icons/svg/record-stop.svg";
import { ReactComponent as RefreshIcon } from "../../assets/icons/svg/record-reload.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/svg/delete-icon.svg";
import { ReactComponent as HighlighterIcon } from "../../assets/icons/svg/highlighter.svg";

export interface RecordingTool {
  id: string;
  icon: React.ReactNode;
  name: string;
  disabled?: boolean;
}

interface ScreenRecordingToolBoxProps {
  isRecording: boolean;
  recordingTime: string;
  hasRecording: boolean;
  isLoading: boolean;
  onToolClick: (toolId: string) => void;
  drawingMode?: boolean;
  maxRecordingSeconds?: number;
  compact?: boolean;
  isFullscreen?: boolean;
}

const ScreenRecordingToolBox: React.FC<ScreenRecordingToolBoxProps> = ({
  isRecording,
  recordingTime,
  hasRecording,
  isLoading,
  onToolClick,
  drawingMode = false,
  maxRecordingSeconds = 30,
  compact = false,
  isFullscreen = false,
}) => {
  const [tooltipInfo, setTooltipInfo] = useState<{ text: string; top: number; left: number } | null>(null);

  // Drag state for non-compact floating toolbox
  const containerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.transition  = 'none';
    el.style.willChange  = 'transform';
    el.style.cursor      = 'grabbing';
    document.body.style.cursor     = 'grabbing';
    document.body.style.userSelect = 'none';
    dragRef.current = { isDragging: true, startX: e.clientX, startY: e.clientY, startLeft: rect.left, startTop: rect.top };
  }, []);

  useEffect(() => {
    // Commit the element's current visual position (including any pending transform) to state
    const commitPos = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect   = el.getBoundingClientRect();
      const finalX = Math.max(0, Math.min(window.innerWidth  - el.offsetWidth,  rect.left));
      const finalY = Math.max(0, Math.min(window.innerHeight - el.offsetHeight, rect.top));
      el.style.cursor      = '';
      el.style.left        = `${finalX}px`;
      el.style.top         = `${finalY}px`;
      el.style.bottom      = 'auto';
      el.style.transform   = '';
      el.style.transition  = '';
      el.style.willChange  = 'auto';
      setPos({ x: finalX, y: finalY });
    };

    const onMouseMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag.isDragging) return;

      // Mouse button released outside the browser — stop drag
      if (e.buttons === 0) {
        drag.isDragging = false;
        document.body.style.cursor     = '';
        document.body.style.userSelect = '';
        commitPos();
        return;
      }

      const el = containerRef.current;
      if (!el) return;
      const clampedLeft = Math.max(0, Math.min(window.innerWidth  - el.offsetWidth,  drag.startLeft + (e.clientX - drag.startX)));
      const clampedTop  = Math.max(0, Math.min(window.innerHeight - el.offsetHeight, drag.startTop  + (e.clientY - drag.startY)));

      // GPU-accelerated — no React re-renders while dragging
      el.style.transform = `translate(${clampedLeft - drag.startLeft}px, ${clampedTop - drag.startTop}px)`;
    };

    const onMouseUp = () => {
      if (!dragRef.current.isDragging) return;
      dragRef.current.isDragging     = false;
      document.body.style.cursor     = '';
      document.body.style.userSelect = '';
      commitPos();
    };

    const onWindowBlur = () => {
      if (!dragRef.current.isDragging) return;
      dragRef.current.isDragging     = false;
      document.body.style.cursor     = '';
      document.body.style.userSelect = '';
      commitPos();
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup",   onMouseUp);
    window.addEventListener("blur",        onWindowBlur);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup",   onMouseUp);
      window.removeEventListener("blur",        onWindowBlur);
    };
  }, []);

  const handleBtnEnter = (e: React.MouseEvent<HTMLButtonElement>, text: string) => {
    if (!isFullscreen) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipInfo({ text, top: rect.top, left: rect.left + rect.width / 2 });
  };

  const handleBtnLeave = () => setTooltipInfo(null);

  const handleToolClick = (toolId: string, disabled: boolean) => {
    if (disabled || isLoading) return;
    onToolClick(toolId);
  };

  const parseSecondsFromTime = (timeStr: string): number => {
    const parts = timeStr.split(":");
    if (parts.length === 2) {
      return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    }
    return maxRecordingSeconds;
  };

  const secondsRemaining = parseSecondsFromTime(recordingTime);

  const isCritical = isRecording && secondsRemaining <= 5;
  const isWarning = isRecording && secondsRemaining <= 10 && secondsRemaining > 5;

  const timerColorClass = isCritical
    ? "text-red-600"
    : isWarning
    ? "text-orange-500"
    : "text-gray-800";

  const progressPercent = isRecording
    ? (secondsRemaining / maxRecordingSeconds) * 100
    : 100;

  const circleRadius = 10;
  const circleCircumference = 2 * Math.PI * circleRadius;
  const strokeDashoffset =
    circleCircumference - (progressPercent / 100) * circleCircumference;

  const progressColor = isCritical
    ? "#DC2626"
    : isWarning
    ? "#F97316"
    : "#22C55E";

  // Compact horizontal layout
  if (compact) {
    return (
      <>
      <div className="flex items-center gap-2">
        {/* Section 1: Delete */}
        <div className="rounded-[8px] border border-[#D7D9E2] px-2 xl:px-3 2xl:px-4 pb-1 xl:pb-2 2xl:pb-3 pt-1.5 xl:pt-2.5 2xl:pt-3.5 shadow-xl" style={{ background: "#FFFEFECC", backdropFilter: "blur(4px)" }}>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToolClick("close", false); }}
            onMouseEnter={(e) => handleBtnEnter(e, "Close")}
            onMouseLeave={handleBtnLeave}
            disabled={isLoading}
            type="button"
            className="hover:scale-105 transition-transform duration-150 group relative"
          >
            <div className="w-7 h-7"><DeleteIcon className="w-full h-full" /></div>
            {!isFullscreen && <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-[11px] font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-lg">Close</span>}
          </button>
        </div>

        {/* Section 2: Main controls — Start/Stop, Timer, Draw, Refresh */}
        <div className="rounded-[8px] border border-[#D7D9E2] px-2  xl:px-2.5 2xl:px-3.5 py-1 xl:py-2 2xl:py-3 flex items-center gap-3 shadow-xl" style={{ background: "#FFFEFECC", backdropFilter: "blur(4px)" }}>
          {/* Start/Stop */}
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToolClick(isRecording ? "stop" : "start", isLoading); }}
            onMouseEnter={(e) => handleBtnEnter(e, isRecording ? "Stop" : "Record")}
            onMouseLeave={handleBtnLeave}
            disabled={isLoading}
            type="button"
            className="hover:scale-105 transition-transform duration-150 group relative"
          >
            <div className="w-9 h-9">
              {isRecording ? <StopIcon className="w-full h-full" /> : <PlayIcon className="w-full h-full" />}
            </div>
            {!isFullscreen && <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-[11px] font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-lg">{isRecording ? "Stop" : "Record"}</span>}
          </button>

          {/* Timer */}
          <div className="flex items-center gap-1">
            {isRecording ? (
              <div className="relative flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 28 28">
                  <circle cx="14" cy="14" r={circleRadius} fill="none" stroke="#E5E7EB" strokeWidth="2.5" />
                  <circle cx="14" cy="14" r={circleRadius} fill="none" stroke={progressColor} strokeWidth="2.5"
                    strokeLinecap="round" strokeDasharray={circleCircumference} strokeDashoffset={strokeDashoffset}
                    transform="rotate(-90 14 14)" style={{ transition: "stroke-dashoffset 1s linear, stroke 0.3s ease" }} />
                  {isCritical && (
                    <circle cx="14" cy="14" r="3" fill="#DC2626" opacity="0.75" className="animate-ping" />
                  )}
                </svg>
              </div>
            ) : (
              <div className="w-7 h-7 flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
            )}
            <div className="flex flex-col leading-none">
              <span className={`text-[12px] font-medium tabular-nums ${timerColorClass} ${isCritical ? "animate-pulse" : ""}`}>{recordingTime}</span>
              {isRecording && <span className="text-[10px] text-gray-400">secs left</span>}
            </div>
          </div>

          {/* Draw — only while recording */}
          {isRecording && (
            <>
              <div className="w-px h-6 bg-gray-200" />
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToolClick("draw", isLoading); }}
                onMouseEnter={(e) => handleBtnEnter(e, "Draw")}
                onMouseLeave={handleBtnLeave}
                disabled={isLoading}
                type="button"
                className={`hover:scale-105 transition-all duration-150 relative group ${drawingMode ? "bg-red-50 rounded-[4px] p-0.5" : ""}`}
              >
                <div className="w-7 h-7 relative">
                  <HighlighterIcon
                    className="w-full h-full transition-colors duration-150"
                    style={{ color: drawingMode ? "#EF4444" : "#4B5563" }}
                  />
                  {drawingMode && (
                    <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse"
                      style={{ boxShadow: "0 0 4px rgba(239,68,68,0.6)" }} />
                  )}
                </div>
                {!isFullscreen && <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-[11px] font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-lg">Draw</span>}
              </button>
            </>
          )}

          <div className="w-px h-6 bg-gray-200" />

          {/* Refresh */}
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToolClick("delete", isLoading); }}
            onMouseEnter={(e) => handleBtnEnter(e, "New")}
            onMouseLeave={handleBtnLeave}
            disabled={isLoading}
            type="button"
            className="hover:scale-105 transition-transform duration-150 pt-0.5 group relative"
          >
            <div className="w-6 h-6"><RefreshIcon className="w-full h-full" /></div>
            {!isFullscreen && <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-[11px] font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-lg">New</span>}
          </button>
        </div>
      </div>

      {isFullscreen && tooltipInfo && createPortal(
        <div
          className="bg-gray-800 text-white text-[11px] font-medium px-2 py-1 rounded-md whitespace-nowrap shadow-lg pointer-events-none"
          style={{ position: "fixed", top: tooltipInfo.top, left: tooltipInfo.left, transform: "translate(-50%, calc(-100% - 6px))", zIndex: 99999 }}
        >
          {tooltipInfo.text}
        </div>,
        document.body
      )}
      </>
    );
  }

  // Position style: use dragged pos if available, otherwise fall back to original fixed classes
  const positionStyle: React.CSSProperties = pos
    ? { position: "fixed", left: pos.x, top: pos.y, bottom: "auto" }
    : {};

  return (
    <div
      ref={containerRef}
      onMouseDown={handleMouseDown}
      className={`flex flex-col items-center gap-2 select-none${pos ? "" : " fixed bottom-[10px] left-[95px] sm:left-[143px] xl:left-[155px] 2xl:left-[169px] 3xl:left-[195px] 4xl:left-[249px]"}`}
      style={{
        zIndex: 9999,
        cursor: dragRef.current.isDragging ? "grabbing" : "grab",
        transition: "none",
        ...positionStyle,
      }}
    >
      {/* Section 1: Start/Stop, Timer, Draw Button, Refresh */}
      <div className="bg-white rounded-[8px] border border-gray-200 p-1 flex flex-col items-center space-y-2 shadow-xl">
        {/* Start/Stop Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleToolClick(isRecording ? "stop" : "start", isLoading);
          }}
          disabled={isLoading}
          aria-label={isRecording ? "Stop Recording" : "Start Recording"}
          type="button"
          className="hover:scale-105 transition-transform duration-150 group relative"
          style={{ cursor: "pointer" }}
        >
          <div className="w-7 h-7 3xl:w-9 3xl:h-9 4xl:w-12 4xl:h-12">
            {isRecording ? (
              <StopIcon className="w-full h-full" />
            ) : (
              <PlayIcon className="w-full h-full" />
            )}
          </div>
          <span className="absolute left-[calc(100%+8px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-[11px] font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-lg z-50">{isRecording ? "Stop" : "Record"}</span>
        </button>

        {/* Timer with circular progress ring */}
        <div className="flex flex-col items-center gap-0.5">
          {isRecording ? (
            <div className="relative flex items-center justify-center">
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                className="3xl:w-9 3xl:h-9 4xl:w-12 4xl:h-12"
              >
                <circle
                  cx="14"
                  cy="14"
                  r={circleRadius}
                  fill="none"
                  stroke="#E5E7EB"
                  strokeWidth="2.5"
                />
                <circle
                  cx="14"
                  cy="14"
                  r={circleRadius}
                  fill="none"
                  stroke={progressColor}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeDasharray={circleCircumference}
                  strokeDashoffset={strokeDashoffset}
                  transform="rotate(-90 14 14)"
                  style={{
                    transition: "stroke-dashoffset 1s linear, stroke 0.3s ease",
                  }}
                />
              </svg>
              {isCritical && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2 h-2 bg-red-600 rounded-full animate-ping opacity-75" />
                </div>
              )}
            </div>
          ) : (
            <div className="w-7 h-7 3xl:w-9 3xl:h-9 flex items-center justify-center">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9CA3AF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
          )}

          <div
            className={`text-[10px] 3xl:text-[12px] 4xl:text-[14px] font-poppins font-medium tabular-nums transition-colors duration-300 ${timerColorClass} ${
              isCritical ? "animate-pulse" : ""
            }`}
          >
            {recordingTime}
          </div>

          {isRecording && (
            <div className="text-[8px] 3xl:text-[9px] text-gray-400 font-poppins leading-none">
              sec left
            </div>
          )}
        </div>

        {/* Drawing Mode Toggle - Only show when recording */}
        {isRecording && (
          <>
            <div className="w-5 border-t border-gray-300 my-1" />

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleToolClick("draw", isLoading);
              }}
              disabled={isLoading}
              aria-label={drawingMode ? "Disable Drawing Mode" : "Enable Drawing Mode"}
              type="button"
              className={`hover:scale-105 transition-all duration-150 relative group ${
                drawingMode ? "bg-red-50 rounded-[4px]" : ""
              }`}
              style={{
                padding: drawingMode ? "4px" : "0",
                cursor: "pointer",
              }}
            >
              <div className="w-5 h-5 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8 relative">
                <HighlighterIcon
                  className="w-full h-full transition-colors duration-150"
                  style={{ color: drawingMode ? "#EF4444" : "#4B5563" }}
                />
                {drawingMode && (
                  <div
                    className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse"
                    style={{
                      boxShadow: "0 0 4px rgba(239, 68, 68, 0.6)",
                    }}
                  />
                )}
              </div>
              <span className="absolute left-[calc(100%+8px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-[11px] font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-lg z-50">Draw</span>
            </button>
          </>
        )}

        <div className="w-5 border-t border-gray-300 my-1" />

        {/* Refresh / New Recording Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleToolClick("delete", isLoading);
          }}
          disabled={isLoading}
          aria-label="New Recording"
          type="button"
          className="hover:scale-105 transition-transform duration-150 group relative"
          style={{ cursor: "pointer" }}
        >
          <div className="w-5 h-5 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8">
            <RefreshIcon className="w-full h-full" />
          </div>
          <span className="absolute left-[calc(100%+8px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-[11px] font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-lg z-50">New</span>
        </button>
      </div>

      {/* Section 2: Close Button */}
      <div className="bg-white rounded-[4px] border border-gray-200 px-2 pt-2 pb-0.5 4xl:px-3.5 4xl:pt-3.5 shadow-xl">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleToolClick("close", false);
          }}
          disabled={isLoading}
          aria-label="Close Recording Tools"
          type="button"
          className="group relative"
          style={{ cursor: "pointer" }}
        >
          <div className="w-5 h-5 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8">
            <DeleteIcon className="w-full h-full" />
          </div>
          <span className="absolute left-[calc(100%+8px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-[11px] font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-lg z-50">Close</span>
        </button>
      </div>
    </div>
  );
};

export default ScreenRecordingToolBox;
